import {
    PropsWithChildren,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import Axios from 'api/client';
import { TwoFactorChallengeDialog } from 'assets/js/components/TwoFactorChallengeDialog';
import { GivebutterAxiosError } from 'types/error';

type TwoFactorContextType = {
    trigger2FA: (methods: Record<string, string>) => Promise<void>;
    resolve2FA: () => void;
};

type TwoFactorPromisePayload = { promise: Promise<void>; resolve?: () => void };

const TwoFactorContext = createContext<TwoFactorContextType | undefined>(undefined);

const useTwoFactor = () => {
    const context = useContext(TwoFactorContext);
    if (!context) {
        throw new Error('useTwoFactor must be used within a withTwoFactor-wrapped component');
    }
    return context;
};

const withTwoFactor = <T extends object>(Component: React.ComponentType<T>) => {
    return (props: T) => {
        const history = useHistory();
        const [hasTwoFactorError, setHasTwoFactorError] = useState(false);
        const [twoFactorMethods, setTwoFactorMethods] = useState<Record<string, string>>();
        const TwoFactorPromise = useRef<TwoFactorPromisePayload | null>(null);

        const twoFactorPromiseFactory = () => {
            const promiseObject: TwoFactorPromisePayload = {} as TwoFactorPromisePayload;

            promiseObject.promise = new Promise<void>(resolve => {
                promiseObject.resolve = resolve;
            });

            TwoFactorPromise.current = promiseObject;
            return promiseObject.promise;
        };

        const trigger2FA = useCallback((methods: Record<string, string>) => {
            setHasTwoFactorError(true);
            setTwoFactorMethods(methods);
            return twoFactorPromiseFactory();
        }, []);

        const resolve2FA = useCallback(() => {
            setHasTwoFactorError(false);
            TwoFactorPromise.current?.resolve?.();
        }, []);

        const ctxValue = useMemo<TwoFactorContextType>(
            () => ({ trigger2FA, resolve2FA }),
            [trigger2FA, resolve2FA],
        );

        return (
            <TwoFactorContext.Provider value={ctxValue}>
                <TwoFactorInterceptor>
                    <TwoFactorChallengeDialog
                        open={hasTwoFactorError}
                        onOpenChange={open => {
                            if (!open) {
                                history.goBack();
                            }
                        }}
                        onSuccess={resolve2FA}
                        twoFactorMethods={twoFactorMethods}
                    />
                    <Component {...props} />
                </TwoFactorInterceptor>
            </TwoFactorContext.Provider>
        );
    };
};

const TwoFactorInterceptor = (props: PropsWithChildren) => {
    const { children } = props;
    const [didLoad, setDidLoad] = useState(false);
    const context = useContext(TwoFactorContext);
    if (!context) {
        throw new Error(
            'TwoFactorInterceptor must be used within a withTwoFactor-wrapped component',
        );
    }
    const { trigger2FA } = context;

    useEffect(() => {
        let interceptor: number;
        if (!didLoad) {
            interceptor = Axios.interceptors.response.use(
                response => response,
                async error => {
                    const axiosError = error as GivebutterAxiosError;

                    if (axiosError.response?.data?.two_factor) {
                        const methods = axiosError.response.data.methods ?? {};
                        const config = axiosError.config;
                        if (config) {
                            await trigger2FA(methods);
                            return Axios(config);
                        }
                    }

                    return Promise.reject(error);
                },
            );
        }
        setDidLoad(true);

        return () => {
            Axios.interceptors.response.eject(interceptor);
        };
    }, [didLoad, trigger2FA]);

    if (!didLoad) {
        return null;
    }

    return <>{children}</>;
};

export { useTwoFactor, withTwoFactor };
